/**
 * Footer - Bottom navigation with previous/next lesson buttons.
 */

import { memo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface FooterProps {
  /** Current lesson number (1-based) */
  currentLessonNumber: number
  /** Total number of lessons */
  totalLessons: number
  /** Whether there's a previous lesson */
  hasPrevious: boolean
  /** Whether there's a next lesson */
  hasNext: boolean
  /** Whether next lesson is unlocked */
  isNextUnlocked: boolean
  /** Callback for previous button */
  onPrevious: () => void
  /** Callback for next button */
  onNext: () => void
  /** Optional className */
  className?: string
}

/**
 * Footer displays lesson navigation controls.
 */
export const Footer = memo(function Footer({
  currentLessonNumber,
  totalLessons,
  hasPrevious,
  hasNext,
  isNextUnlocked,
  onPrevious,
  onNext,
  className,
}: FooterProps) {
  const canGoNext = hasNext && isNextUnlocked

  return (
    <footer
      className={cn(
        'sticky bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80',
        className
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          {/* Previous button */}
          <button
            type="button"
            onClick={onPrevious}
            disabled={!hasPrevious}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              hasPrevious
                ? 'text-foreground hover:bg-muted'
                : 'cursor-not-allowed text-muted-foreground/50'
            )}
            aria-label="Go to previous lesson"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {/* Lesson indicator */}
          <div className="flex items-center gap-2">
            {/* Progress dots for small screens */}
            <div className="flex gap-1 sm:hidden">
              {Array.from({ length: Math.min(totalLessons, 8) }).map((_, idx) => (
                <span
                  key={idx}
                  className={cn(
                    'h-2 w-2 rounded-full transition-colors',
                    idx + 1 === currentLessonNumber
                      ? 'bg-primary'
                      : idx + 1 < currentLessonNumber
                        ? 'bg-green-500'
                        : 'bg-muted-foreground/30'
                  )}
                />
              ))}
              {totalLessons > 8 && (
                <span className="text-xs text-muted-foreground">...</span>
              )}
            </div>

            {/* Text indicator for larger screens */}
            <span className="hidden text-sm text-muted-foreground sm:inline">
              Lesson{' '}
              <span className="font-medium text-foreground">
                {currentLessonNumber}
              </span>{' '}
              of{' '}
              <span className="font-medium text-foreground">{totalLessons}</span>
            </span>
          </div>

          {/* Next button */}
          <button
            type="button"
            onClick={onNext}
            disabled={!canGoNext}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              canGoNext
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'cursor-not-allowed bg-muted text-muted-foreground/50'
            )}
            aria-label={
              !hasNext
                ? 'No more lessons'
                : !isNextUnlocked
                  ? 'Complete current lesson to unlock'
                  : 'Go to next lesson'
            }
            title={
              !isNextUnlocked && hasNext
                ? 'Complete current lesson to unlock'
                : undefined
            }
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </footer>
  )
})
